import mes_tab_data from "../assets/mockedData/mes_tab_data";

/** IDs from the `_UserRole` table. */
export const UserRoleId = {
  SystemAdmin: 1,
  Admin: 2,
  Radnik: 31,
  Skladistar: 40,
} as const;

/** Top-level modules of the icon rail, keyed by their route segment. */
export type ModuleKey =
  | "dashboard"
  | "devices"
  | "develop"
  | "data"
  | "mes"
  | "organization"
  | "marketplace"
  | "settings"
  | "support";

export type RoleAccess = {
  modules: ModuleKey[];
  /** `title` values from `mes_tab_data`. An empty list means every section. */
  mesSections: string[];
  /** Routes that are reachable but have no menu entry, e.g. legacy aliases. */
  extraPaths?: string[];
  landingPath: string;
};

/**
 * Only roles listed here are restricted. A role that is absent — including a
 * user with no role at all — keeps full access, so adding a role to the
 * `_UserRole` table never locks anyone out until it is configured here.
 */
const ROLE_ACCESS: Record<number, RoleAccess> = {
  [UserRoleId.Skladistar]: {
    modules: ["mes"],
    mesSections: ["Skladiste"],
    extraPaths: ["/mes/primke"],
    landingPath: "/mes/receive-delivery",
  },
};

/** Reachable for every role; these hang off the app bar, not the module rail. */
const ALWAYS_ALLOWED_PATHS = ["/profile", "/notifications"];

export const DEFAULT_LANDING_PATH = "/dashboard";
export const DEFAULT_MES_LANDING_PATH = "/mes/planning";

export function getRoleAccess(roleId?: number | null): RoleAccess | null {
  if (roleId == null) return null;
  return ROLE_ACCESS[roleId] ?? null;
}

export function canAccessModule(
  access: RoleAccess | null,
  module: ModuleKey
): boolean {
  if (!access) return true;
  return access.modules.includes(module);
}

function isMesSectionAllowed(access: RoleAccess, title: string): boolean {
  return access.mesSections.length === 0 || access.mesSections.includes(title);
}

/**
 * Drops MES sections the role may not see. Sidebars of other modules are left
 * untouched — those are gated by module access instead.
 */
export function filterSidebarSections(
  sections: any[],
  access: RoleAccess | null
): any[] {
  if (!access || !Array.isArray(sections)) return sections;
  return sections.filter((section) => {
    const path: string = section?.path ?? "";
    if (!path.startsWith("/mes")) return true;
    return isMesSectionAllowed(access, section?.title ?? "");
  });
}

/** Where `/mes` should land for this role. */
export function getMesLandingPath(access: RoleAccess | null): string {
  if (!access) return DEFAULT_MES_LANDING_PATH;
  const firstAllowed = filterSidebarSections(mes_tab_data, access)[0];
  return firstAllowed?.path ?? DEFAULT_MES_LANDING_PATH;
}

function collectAllowedPaths(access: RoleAccess): {
  exact: string[];
  prefixes: string[];
} {
  const exact: string[] = [];
  const prefixes = [...ALWAYS_ALLOWED_PATHS, ...(access.extraPaths ?? [])];

  access.modules.forEach((module) => {
    if (module !== "mes") {
      prefixes.push(`/${module}`);
      return;
    }
    // `/mes` only redirects onwards, so it is allowed on its own without
    // opening up every `/mes/*` route below it.
    exact.push("/mes");
    filterSidebarSections(mes_tab_data, access).forEach((section) => {
      if (section?.path) prefixes.push(section.path);
      (section?.subItems ?? []).forEach((sub) => {
        if (sub?.path) prefixes.push(sub.path);
      });
    });
  });

  return { exact, prefixes };
}

export function canAccessPath(
  access: RoleAccess | null,
  pathname: string
): boolean {
  if (!access) return true;

  const path =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.replace(/\/+$/, "")
      : pathname;

  const { exact, prefixes } = collectAllowedPaths(access);
  if (exact.includes(path)) return true;
  return prefixes.some(
    (allowed) => path === allowed || path.startsWith(`${allowed}/`)
  );
}
